import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import analyticsApi from '../../services/analyticsapi';
import MetricCard from '../../components/analytics/dashboard/MetricCard';
import TopSellerDetails from '../../components/analytics/dashboard/TopSellerDetails';
import DeadStockList from '../../components/analytics/dashboard/DeadStockList';
import OutOfStockList from '../../components/analytics/dashboard/OutOfStockList';
import SalesBreakdown from '../../components/analytics/dashboard/SalesBreakdown';
import YearlySalesModal from '../../components/analytics/dashboard/YearlySalesModal';
import VendorStatementModal from '../../components/analytics/dashboard/VendorStatementModal';

const AnalyticsDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [topSellerModalOpen, setTopSellerModalOpen] = useState(false);
  const [deadStockModalOpen, setDeadStockModalOpen] = useState(false);
  const [outOfStockModalOpen, setOutOfStockModalOpen] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [vendorStatementModalOpen, setVendorStatementModalOpen] = useState(false);
  
  // Data states for modals
  const [topSellerDetails, setTopSellerDetails] = useState(null);
  const [deadStockList, setDeadStockList] = useState(null);
  const [outOfStockList, setOutOfStockList] = useState(null);
  const [salesBreakdown, setSalesBreakdown] = useState(null);
  // Modal for yearly sales
  const [yearlySalesModalOpen, setYearlySalesModalOpen] = useState(false);
  
  // Action handlers
  const handleViewTopSeller = async () => {
    try {
      if (metrics?.top_seller) {
        setLoading(true);
        const response = await analyticsApi.viewTopSellerDetails(metrics.top_seller.product_id);
        setTopSellerDetails(response.data);
        setTopSellerModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching top seller details:", err);
      setError("Failed to load top seller details.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewDeadStock = async () => {
    try {
      setLoading(true);
      const response = await analyticsApi.viewDeadStockList();
      console.log(response.data);
      
      setDeadStockList(response.data);
      setDeadStockModalOpen(true);
    } catch (err) {
      console.error("Error fetching dead stock list:", err);
      setError("Failed to load dead stock list.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewOutOfStock = async () => {
    try {
      setLoading(true);
      const response = await analyticsApi.viewOutOfStockList();
      console.log(response.data);
      
      setOutOfStockList(response.data);
      setOutOfStockModalOpen(true);
    } catch (err) {
      console.error("Error fetching out of stock list:", err);
      setError("Failed to load out of stock list.");
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleViewSalesBreakdown = async () => {
    try {
      if (metrics?.sales_this_month) {
        setLoading(true);
        const response = await analyticsApi.viewSalesBreakdown(metrics.sales_this_month.period);
        setSalesBreakdown(response.data);
        setSalesModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching sales breakdown:", err);
      setError("Failed to load sales breakdown.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle viewing yearly sales graph
  const handleViewYearlySales = () => {
    setYearlySalesModalOpen(true);
  };
  
  // Handle viewing vendor statements
  const handleViewVendorStatements = () => {
    setVendorStatementModalOpen(true);
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const response = await analyticsApi.getDashboardMetrics();
        setMetrics(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching dashboard metrics:", err);
        setError("Failed to load dashboard metrics. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchMetrics();
  }, []);

  return (
    <Box sx={{ padding: 3 }}>
      <Breadcrumbs items={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Analytics' }
      ]} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 500, color: '#333' }}>
          Analytics Dashboard
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !metrics ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Top metrics row */}
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard 
              title="Top Seller"
              value={metrics?.top_seller ? `${metrics.top_seller.product_name} (${metrics.top_seller.units_sold} units)` : 'No data'}
              actionText="View Details"
              onAction={handleViewTopSeller}
              color="#4caf50"
            />
          </Grid>
          
          {/* Dead Stock */}
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard 
              title="Dead Stock"
              value={metrics?.dead_stock ? `${metrics.dead_stock.count} SKUs` : 'No data'}
              actionText="View List"
              onAction={handleViewDeadStock}
              color="#ff9800"
            />
          </Grid>
          
          {/* Out of Stock */}
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard 
              title="Out of Stock"
              value={metrics?.out_of_stock ? `${metrics.out_of_stock.total_count} SKUs` : 'No data'}
              actionText={metrics?.out_of_stock?.action || "View Out of Stock"}
              onAction={handleViewOutOfStock}
              color="#e91e63"
            />
          </Grid>
          
          {/* Sales This Month */}
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard 
              title="Sales This Month"
              value={metrics?.sales_this_month ? `₹ ${metrics.sales_this_month.total_amount.toLocaleString('en-IN')}` : 'No data'}
              actionText="View Breakdown"
              onAction={handleViewSalesBreakdown}
              color="#2196f3"
            />
          </Grid>
          
          {/* Yearly Sales */}
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard 
              title="Yearly Sales Trend"
              value="Yearly Sale Chart"
              actionText={metrics?.yearly_sales_graph?.action || "View Yearly Sales"}
              onAction={handleViewYearlySales}
              color="#9c27b0"
            />
          </Grid>
          
          {/* Vendor Statements Analytics */}
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard 
              title="Vendor Statements"
              value="Financial"
              actionText="View Analysis"
              onAction={handleViewVendorStatements}
              color="#607d8b"
            />
          </Grid>
        </Grid>
      )}
      
      {/* Modals for detailed views */}
      <TopSellerDetails 
        open={topSellerModalOpen}
        onClose={() => setTopSellerModalOpen(false)}
        data={topSellerDetails}
        fullScreen={true}
      />
      
      <DeadStockList 
        open={deadStockModalOpen}
        onClose={() => setDeadStockModalOpen(false)}
        data={deadStockList}
        fullScreen={true}
      />
      
      <OutOfStockList 
        open={outOfStockModalOpen}
        onClose={() => setOutOfStockModalOpen(false)}
        data={outOfStockList}
        fullScreen={true}
      />
      
      <SalesBreakdown 
        open={salesModalOpen}
        onClose={() => setSalesModalOpen(false)}
        data={salesBreakdown}
        fullScreen={true}
      />
      
      <YearlySalesModal
        open={yearlySalesModalOpen}
        onClose={() => setYearlySalesModalOpen(false)}
      />
      
      <VendorStatementModal
        open={vendorStatementModalOpen}
        onClose={() => setVendorStatementModalOpen(false)}
      />
    </Box>
  );
};

export default AnalyticsDashboard;
